import { useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import {
  ADULTS_ONLY_MESSAGE,
  HANDLE_PATTERN,
  HANDLE_SHAPE_MESSAGE,
  PROFILE_CARD,
} from '@dove/shared';
import { countFrom } from '@/components/CharCounter';
import { useDebounced } from '@/lib/debounced';
import { Field } from '@/components/Field';
import { PlaceField } from '@/components/profile/PlaceField';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { checkHandle } from '@/lib/api';
import { keys } from '@/lib/query-client';

/**
 * THE FIELDS A PERSON FILLS IN ABOUT THEMSELVES, AND THE ONLY COPY OF THEM.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * There are two forms in this product that collect a card — the one that creates
 * an account and the one that edits it — and they were going to be two forms. That
 * is the arrangement where the signup screen accepts 40 characters of name and the
 * edit screen accepts 50, where one lowercases a handle as it is typed and the
 * other does not, and where the availability check exists on the first and never
 * reaches the second. So the fields are here, once, and the screens supply the
 * values and the button.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * WHAT DIFFERS BETWEEN THE TWO, AND IT IS EXACTLY TWO FIELDS.
 *
 * The **date of birth** is asked once and never again: it is the 18+ gate (docs/8
 * § A), it is never sent back to a client, and nothing in the product renders a
 * number from it — so an edit form has nothing to put in the field and no reason
 * to. A birthday somebody can revise is also not a gate.
 *
 * The **bird's name** is the mirror image. Nobody is asked for it at signup —
 * every bird arrives already named (`defaultBirdName`, docs/5 § D260805-0242) — because it
 * is a question about an object the person has not met yet, at minute two, and the
 * answer most people would type is a placeholder. Renaming is an edit.
 *
 * WHAT IS NOT HERE, AND MUST NOT ARRIVE: gender, seeking, interests, religion,
 * observance, "what are you looking for". docs/5 § D260803-1552 took the dating panel off
 * the card, and a form is where that sort of thing walks back in one reasonable
 * field at a time.
 *
 * THE ORDER IS THE SAME IN BOTH FORMS. Username, name, the one field that differs,
 * city, bio — so that somebody who filled this in at signup is looking at the same
 * form in the same order when they come back to change one line of it.
 */

export interface CardDraft {
  /** Lowercased as it is typed; the pattern and the column are both lowercase. */
  handle: string;
  name: string;
  /** `YYYY-MM-DD`. Create only. */
  dob: string;
  /**
   * The city as it will be saved — `'Doha, Qatar'`. DETECTED, NEVER TYPED
   * (docs/5 § D260809-1521): the only writers are `PlaceField` and, at signup, the
   * edge's suggestion. The empty string is "there is none", which is a saveable
   * state on both forms.
   */
  city: string;
  bio: string;
  /** Edit only. Empty means "use the default", not "no name". */
  birdName: string;
}

export type CardProblems = Partial<Record<keyof CardDraft, string>>;

/**
 * What is wrong with a draft, as the sentence the field will show.
 *
 * ONE FUNCTION FOR BOTH FORMS AND FOR BOTH JOBS — deciding whether the button is
 * live, and deciding what a field says. Two implementations of "is this valid" is
 * how a form ends up with an enabled button that submits something the server
 * refuses.
 *
 * IT ONLY EVER SPEAKS ABOUT FIELDS THAT HAVE SOMETHING IN THEM. A red line under
 * an untouched box is the form telling somebody off for not having started; a
 * missing required field is expressed by the button not being live, and by the
 * label that is already there saying what is wanted.
 */
export function cardProblems(draft: CardDraft, mode: Mode): CardProblems {
  const problems: CardProblems = {};

  if (draft.handle && !HANDLE_PATTERN.test(draft.handle)) {
    problems.handle = HANDLE_SHAPE_MESSAGE;
  }
  if (draft.name.length > PROFILE_CARD.DISPLAY_NAME_MAX_CHARS) {
    problems.name = 'Too long.';
  }
  /* The date field's own `max` stops the picker producing this, so it is only
     reachable by typing — which is exactly why it still needs a sentence. */
  if (mode === 'create' && draft.dob && !isAdult(draft.dob)) {
    problems.dob = ADULTS_ONLY_MESSAGE;
  }
  if (draft.city.length > PROFILE_CARD.CITY_MAX_CHARS) problems.city = 'Too long.';
  if (draft.bio.length > PROFILE_CARD.BIO_MAX_CHARS) problems.bio = 'Too long.';
  if (draft.birdName.length > PROFILE_CARD.BIRD_NAME_MAX_CHARS) {
    problems.birdName = 'Too long.';
  }
  return problems;
}

/** Whether the required fields are there at all, which is what makes the button
 *  live. Separate from `cardProblems` because "not yet" is not "wrong". */
export function cardIsComplete(draft: CardDraft, mode: Mode): boolean {
  if (!draft.name.trim()) return false;
  if (mode === 'create' && (!draft.handle || !draft.dob)) return false;
  return Object.keys(cardProblems(draft, mode)).length === 0;
}

/**
 * Whether a date of birth clears the age gate, compared as CALENDAR DATES.
 *
 * Not by dividing a millisecond difference by the length of a year: that
 * arithmetic is off by a day around leap years, and this is a gate somebody sits
 * exactly on for one day of their life. The same comparison the server makes.
 */
function isAdult(dob: string): boolean {
  const [y, m, d] = dob.split('-').map(Number);
  if (!y || !m || !d) return false;
  const birthday = Date.UTC(y + PROFILE_CARD.MINIMUM_AGE_YEARS, m - 1, d);
  return birthday <= Date.now();
}

/**
 * The latest date of birth that clears the gate, as `YYYY-MM-DD`.
 *
 * IT IS THE DATE FIELD'S `max`, AND THAT IS A UX FIX RATHER THAN A VALIDATION ONE.
 * A `type="date"` picker opens on today, so somebody born in 1994 begins thirty-two
 * years of scrolling; bounding it lands the picker in a plausible decade instead.
 * It also means the picker cannot produce a refusable date at all, which is the
 * cheapest possible version of an age gate: not a refusal, an absence.
 */
export function latestAdultDob(now = new Date()): string {
  const at = new Date(
    Date.UTC(
      now.getUTCFullYear() - PROFILE_CARD.MINIMUM_AGE_YEARS,
      now.getUTCMonth(),
      now.getUTCDate(),
    ),
  );
  return at.toISOString().slice(0, 10);
}

export type Mode = 'create' | 'edit';

/* --------------------------------------------------------------------------
 * Is this name free
 * ----------------------------------------------------------------------- */

export type HandleState =
  | { kind: 'quiet' }
  | { kind: 'checking' }
  | { kind: 'free' }
  | { kind: 'taken'; message: string };

/**
 * Ask the server whether a handle is free, as cheaply as it can be asked.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FOUR THINGS KEEP THE REQUEST COUNT DOWN, and they compose:
 *
 * 1. **Debounced.** Nothing is asked while somebody is still typing. `yasmin` is
 *    one request, not six.
 * 2. **Nothing malformed is asked.** The pattern lives on the client too, so
 *    `ya` and `Yasmin!` are answered locally — the server is only asked about
 *    strings that could actually be granted.
 * 3. **Never asked about the name you are wearing.** The commonest state of this
 *    field on an edit screen is "unchanged", and unchanged is free by definition.
 * 4. **Cached forever, per string** (`staleTime: Infinity`). Backspacing back
 *    through a name asks nothing, and returning to the field asks nothing.
 *
 * On the server it is one statement and two index probes (see `handleAvailability`),
 * so the whole check costs about what a keystroke should.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * A STALE ANSWER CANNOT BE SHOWN. The query is keyed by the candidate, so the
 * answer to `yasmi` is not a value this hook can return once the field says
 * `yasmin` — which is the bug the naïve version of this always has, where a slow
 * response overwrites a fast one and the field reports on a name nobody is asking
 * about.
 *
 * IT IS ADVICE, NOT A RESERVATION. Nothing is held; the UNIQUE index decides it at
 * claim time, and the form is written to survive being told "taken" on submit
 * anyway.
 */
export function useHandleAvailability(
  candidate: string,
  /** The handle this account already has, if any. */
  current?: string,
): HandleState {
  const settled = useDebounced(candidate, DEBOUNCE_MS);

  const worth =
    settled.length > 0 &&
    HANDLE_PATTERN.test(settled) &&
    settled !== current &&
    /* Only ask about what is on screen now. Between a keystroke and the debounce
       settling, `settled` is a previous value and its answer is not about this
       field. */
    settled === candidate;

  const asked = useQuery({
    queryKey: keys.handle(settled),
    queryFn: () => checkHandle(settled),
    enabled: worth,
    staleTime: Infinity,
    retry: false,
  });

  if (!candidate || !HANDLE_PATTERN.test(candidate)) return { kind: 'quiet' };
  /* Your own name is yours. Said locally rather than asked, so the field does not
     flicker through "checking" on a screen nobody has typed into. */
  if (candidate === current) return { kind: 'free' };
  if (settled !== candidate || asked.isFetching) return { kind: 'checking' };
  /* A failed check is not a refusal. The claim itself is still guarded by the
     UNIQUE index, so the honest thing is to say nothing and let the button work. */
  if (!asked.data) return { kind: 'quiet' };
  return asked.data.available
    ? { kind: 'free' }
    : { kind: 'taken', message: asked.data.message ?? HANDLE_SHAPE_MESSAGE };
}

/** Long enough to cover a typed word, short enough that a considered pause gets an
 *  answer before the thumb moves to the next field. */
const DEBOUNCE_MS = 400;

/* --------------------------------------------------------------------------
 * The fields
 * ----------------------------------------------------------------------- */

export function CardFields({
  mode,
  draft,
  onChange,
  problems,
  handleState,
}: {
  mode: Mode;
  draft: CardDraft;
  onChange: (patch: Partial<CardDraft>) => void;
  problems: CardProblems;
  handleState: HandleState;
}) {
  return (
    <div className="flex flex-col gap-3">
      <Field
        label="Username"
        /* The shape complaint wins over the availability one: a malformed name
           was never asked about, so "taken" would be a stale answer. */
        error={
          problems.handle ??
          (handleState.kind === 'taken' ? handleState.message : null)
        }
      >
        {(props) => (
          <div className="relative">
            {/* The `@` is part of the address rather than something to type, so it
                is drawn into the field. `ps-8` clears it. */}
            <span
              aria-hidden
              className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-[15px] text-muted-foreground"
            >
              @
            </span>
            <Input
              {...props}
              value={draft.handle}
              /* Lowercased as it is typed, because the pattern is lowercase and the
                 server folds it anyway — a field that silently rejects a capital
                 letter it accepted the keystroke for is a field fighting the
                 person. Spaces are dropped rather than rejected: a handle cannot
                 contain one, so a typed space is always a mistake. */
              onChange={(e) =>
                onChange({
                  handle: e.target.value.toLowerCase().replace(/\s+/g, ''),
                })
              }
              placeholder="handle"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="text"
              enterKeyHint="next"
              maxLength={PROFILE_CARD.HANDLE_MAX_CHARS}
              className="ps-8 pe-9"
              required={mode === 'create'}
            />
            {/*
              THE ONLY POSITIVE SIGNAL ON THIS FORM, AND IT IS NOT A SENTENCE.
              A tick, at the end of the field, when the name is free. "That name is
              available!" under the control would be the explanatory copy docs/1 §
              Copy in the interface bans — and a tick is what every username field
              anybody has used already means by it.

              NOTHING IS DRAWN WHILE CHECKING. A spinner is unprompted indefinite
              motion in a form (docs/1 bans animation over 400ms and this app has no
              spinner anywhere, by rule — see `states.tsx`), and it would flash for
              a few hundred milliseconds on every field somebody types into. The
              honest rendering of "we do not know yet" is nothing.
            */}
            {handleState.kind === 'free' && !problems.handle && (
              <Check
                aria-hidden
                className="pointer-events-none absolute end-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              />
            )}
          </div>
        )}
      </Field>

      <Field
        label="Name"
        error={problems.name}
        counter={{
          remaining: PROFILE_CARD.DISPLAY_NAME_MAX_CHARS - draft.name.length,
          show: draft.name.length >= countFrom(PROFILE_CARD.DISPLAY_NAME_MAX_CHARS),
        }}
      >
        {(props) => (
          <Input
            {...props}
            value={draft.name}
            onChange={(e) => onChange({ name: e.target.value })}
            autoComplete="name"
            autoCapitalize="words"
            enterKeyHint="next"
            maxLength={PROFILE_CARD.DISPLAY_NAME_MAX_CHARS}
            required
          />
        )}
      </Field>

      {mode === 'create' ? (
        <Field label="Date of birth" error={problems.dob}>
          {(props) => (
            <Input
              {...props}
              type="date"
              value={draft.dob}
              onChange={(e) => onChange({ dob: e.target.value })}
              autoComplete="bday"
              /* Bounded at both ends so the picker opens somewhere plausible
                 instead of on today. See `latestAdultDob`. */
              max={latestAdultDob()}
              min="1900-01-01"
              required
            />
          )}
        </Field>
      ) : (
        <Field
          label="Your bird’s name"
          error={problems.birdName}
          counter={{
            remaining: PROFILE_CARD.BIRD_NAME_MAX_CHARS - draft.birdName.length,
            show:
              draft.birdName.length >= countFrom(PROFILE_CARD.BIRD_NAME_MAX_CHARS),
          }}
        >
          {(props) => (
            <Input
              {...props}
              value={draft.birdName}
              onChange={(e) => onChange({ birdName: e.target.value })}
              /* NO PLACEHOLDER, because the field is never empty when it opens:
                 every bird has a name and the server always sends one, so what
                 loads is what the bird is called. Emptying it and saving hands the
                 bird back to its default (docs/5 § D260805-0242). */
              autoCapitalize="words"
              enterKeyHint="next"
              maxLength={PROFILE_CARD.BIRD_NAME_MAX_CHARS}
            />
          )}
        </Field>
      )}

      {/*
        THE CITY IS DETECTED RATHER THAN TYPED (docs/5 § D260809-1521), so there is no
        counter on it and no `maxLength`: nobody is filling a box, and the one
        function that composes the value already fits it to `CITY_MAX_CHARS`.

        Still city-level and nothing finer (docs/5 § D260731-0207) — the device's fix is
        rounded before it is looked up and the coordinates reach nothing.
      */}
      <PlaceField
        value={draft.city}
        onChange={(city) => onChange({ city })}
        error={problems.city}
      />

      <Field
        label="Bio"
        optional
        error={problems.bio}
        counter={{
          remaining: PROFILE_CARD.BIO_MAX_CHARS - draft.bio.length,
          show: draft.bio.length >= countFrom(PROFILE_CARD.BIO_MAX_CHARS),
        }}
      >
        {(props) => (
          <Textarea
            {...props}
            value={draft.bio}
            onChange={(e) => onChange({ bio: e.target.value })}
            rows={3}
            maxLength={PROFILE_CARD.BIO_MAX_CHARS}
          />
        )}
      </Field>
    </div>
  );
}
